import { useEffect, useRef } from 'react';
import { ICurrentCategoryData } from '../cus-product-list.data';
import { CATEGORY_NAV_DRAG_THRESHOLD_PX } from '../cus-product-list.const';

export default function useCategoryNavigation({ response }: Pick<ICurrentCategoryData, 'response'>) {
    const { currentCategory, categoriesFamily, parentCategory } = response?.result ?? {
        currentCategory: null,
        categoriesFamily: [],
        parentCategory: null
    };

    const containerRef = useRef<HTMLUListElement | null>(null);

    useEffect(() => {
        if (!containerRef.current) {
            return;
        }

        const container = containerRef.current;

        let isPointerDown = false;
        let startX = 0;
        let scrollStart = 0;
        let hasDragged = false;

        const isInteractiveElement = (el: EventTarget | null): boolean => {
            if (!(el instanceof HTMLElement)) {
                return false;
            }

            return !!el.closest('a, button, input, textarea, select');
        };

        const onMouseDown = (e: MouseEvent) => {
            hasDragged = false;

            if (!isInteractiveElement(e.target)) {
                isPointerDown = true;
                startX = e.pageX;
                scrollStart = container.scrollLeft;
            }
        };

        const onMouseMove = (e: MouseEvent) => {
            if (!isPointerDown) {
                return;
            }

            const deltaX = e.pageX - startX;

            if (Math.abs(deltaX) > CATEGORY_NAV_DRAG_THRESHOLD_PX) {
                hasDragged = true;
                container.classList.add('is-dragging');
                container.scrollLeft = scrollStart - deltaX;
                e.preventDefault();
            }
        };

        const stopDrag = () => {
            isPointerDown = false;
            container.classList.remove('is-dragging');
        };

        const onClickCapture = (e: MouseEvent) => {
            if (hasDragged) {
                e.preventDefault();
                e.stopPropagation();
                hasDragged = false;
            }
        };

        container.addEventListener('mousedown', onMouseDown);
        container.addEventListener('mousemove', onMouseMove);
        window.addEventListener('mouseup', stopDrag);
        container.addEventListener('click', onClickCapture, true);

        return () => {
            container.removeEventListener('mousedown', onMouseDown);
            container.removeEventListener('mousemove', onMouseMove);
            window.removeEventListener('mouseup', stopDrag);
            container.removeEventListener('click', onClickCapture, true);
        };
    }, []);

    return { containerRef, currentCategory, categoriesFamily, parentCategory };
}
